import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";

const HelpCtx = createContext(null);

/** App-wide help state: the Ask Kingly slide-over, its chat history, and the active guided walkthrough. */
export function HelpProvider({ user, children }) {
  const navigate = useNavigate();
  const [askOpen, setAskOpen] = useState(false);
  const [askPrefill, setAskPrefill] = useState("");
  const [messages, setMessages] = useState([]);
  const [guide, setGuide] = useState(null);

  const library = useQuery({
    queryKey: ["help-articles"],
    queryFn: async () => (await api.get("/help/articles")).data,
    enabled: !!user,
    staleTime: Infinity,
  });

  const goTo = useCallback((route) => {
    setAskOpen(false);
    if (route) navigate(route);
  }, [navigate]);

  const startGuide = useCallback((article) => {
    if (!article?.steps?.length) return;
    setAskOpen(false);
    setGuide({ article, step: 0 });
  }, []);

  const value = useMemo(() => ({
    askOpen, openAsk: (prefill) => { setAskPrefill(typeof prefill === "string" ? prefill : ""); setAskOpen(true); },
    closeAsk: () => setAskOpen(false), setAskOpen,
    askPrefill, clearPrefill: () => setAskPrefill(""),
    messages, setMessages, clearMessages: () => setMessages([]),
    guide, setGuide, startGuide, endGuide: () => setGuide(null),
    goTo, library: library.data, libraryLoading: library.isLoading,
  }), [askOpen, askPrefill, messages, guide, startGuide, goTo, library.data, library.isLoading]);

  return <HelpCtx.Provider value={value}>{children}</HelpCtx.Provider>;
}

export function useHelp() {
  return useContext(HelpCtx) || {};
}

/** Article whose route/steps best match the current page — powers Ask Kingly's suggestions. */
export function articlesForPath(library, path) {
  if (!library) return [];
  const areaId = (library.route_areas || []).find(([prefix]) => path.startsWith(prefix))?.[1] || "start";
  return library.articles.filter((a) => a.area === areaId);
}
